import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { StructuredData, generateHowToSchema } from '@/components/StructuredData';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Search, 
  Rocket, 
  Package, 
  TrendingUp, 
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Target,
  Zap,
  RefreshCw
} from 'lucide-react';
import { entityBlueprint } from '@/config/entity-blueprint';

// Challenge 100 phases with detailed info
const CHALLENGE_PHASES = [
  {
    id: 'spy100',
    icon: Search,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'launch100',
    icon: Rocket,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
  {
    id: 'ship100',
    icon: Package,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  {
    id: 'grow100',
    icon: TrendingUp,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
];

// Comparison data
const COMPARISON_KEYS = [
  'approach',
  'speed',
  'iteration',
  'validation',
  'risk',
];

export default function Methodology() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedPath();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  // Get methodology steps for HowTo schema
  const howToSteps = CHALLENGE_PHASES.map(phase => ({
    name: t(`methodology.phases.${phase.id}.title`),
    text: t(`methodology.phases.${phase.id}.description`),
  }));

  const howToSchema = generateHowToSchema(
    t('methodology.howToTitle', 'How to Build Products with Challenge 100'),
    t('methodology.howToDescription', 'A systematic methodology for building products through four complementary phases'),
    howToSteps
  );

  return (
    <div className="min-h-screen bg-background">
      <Navigation isHomePage={false} />
      
      {/* Page Schema */}
      <StructuredData type="website" />
      
      {/* HowTo Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      <main className="container mx-auto px-4 py-24 max-w-6xl">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '..' })}
          className="mb-8 -ml-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back', 'Back')}
        </Button>

        {/* Header */}
        <header className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t('methodology.badge', 'Methodology')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('methodology.title', 'Challenge 100 Methodology')}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('methodology.subtitle', 'A systematic framework for building products through Spy, Launch, Ship, and Grow phases.')}
          </p>
        </header>

        {/* Entity anchor */}
        <div className="bg-muted/30 rounded-lg p-4 mb-12 text-sm text-muted-foreground text-center">
          {lang === 'zh' ? (
            <p>
              Challenge 100 方法论由 <strong>{entityBlueprint.founder.name}</strong> 于
              {entityBlueprint.brand.foundingDate}年创建，融合了他
              {entityBlueprint.metrics.yearsProduct.value}年产品经验。
            </p>
          ) : (
            <p>
              Challenge 100 methodology was created by <strong>{entityBlueprint.founder.nameEn}</strong> in 
              {entityBlueprint.brand.foundingDate}, incorporating {entityBlueprint.metrics.yearsProduct.value} years of product experience.
            </p>
          )}
        </div>

        {/* Core Philosophy */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6 text-center">
            {t('methodology.philosophyTitle', 'Core Philosophy')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Lightbulb, key: 'experienceAsProduct' },
              { icon: Zap, key: 'smallSteps' },
              { icon: RefreshCw, key: 'aiNative' },
            ].map(item => (
              <Card key={item.key} className="text-center">
                <CardHeader>
                  <item.icon className="mx-auto h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">
                    {t(`methodology.philosophy.${item.key}.title`)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {t(`methodology.philosophy.${item.key}.description`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Four Phases */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {t('methodology.phasesTitle', 'Four Phases of Challenge 100')}
          </h2>
          
          {/* Phase Flow */}
          <div className="flex flex-wrap justify-center items-center gap-4 mb-12">
            {CHALLENGE_PHASES.map((phase, index) => (
              <div key={phase.id} className="flex items-center">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${phase.bgColor}`}>
                  <phase.icon className={`h-5 w-5 ${phase.color}`} />
                  <span className="font-medium">{t(`methodology.phases.${phase.id}.title`)}</span>
                </div>
                {index < CHALLENGE_PHASES.length - 1 && (
                  <ArrowRight className="mx-2 h-5 w-5 text-muted-foreground hidden md:block" />
                )}
              </div>
            ))}
          </div>

          {/* Detailed Phase Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {CHALLENGE_PHASES.map(phase => (
              <Card key={phase.id} className="overflow-hidden">
                <CardHeader className={phase.bgColor}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-background`}>
                      <phase.icon className={`h-6 w-6 ${phase.color}`} />
                    </div>
                    <div>
                      <CardTitle>{t(`methodology.phases.${phase.id}.title`)}</CardTitle>
                      <CardDescription>
                        {t(`methodology.phases.${phase.id}.subtitle`, '')}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground mb-4">
                    {t(`methodology.phases.${phase.id}.description`)}
                  </p>
                  <div className="space-y-2">
                    {(t(`methodology.phases.${phase.id}.steps`, { returnObjects: true }) as string[] || []).map((step, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className={`h-4 w-4 mt-0.5 ${phase.color}`} />
                        <span className="text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="mt-6 w-full"
                    onClick={() => navigate({ to: getLocalizedPath(`/${phase.id}`) })}
                  >
                    {t('methodology.explorePhase', 'Explore')} {t(`methodology.phases.${phase.id}.title`)}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {t('methodology.comparisonTitle', 'Challenge 100 vs Traditional Approach')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-4 font-medium">
                    {t('methodology.comparison.aspect', 'Aspect')}
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-primary">
                    Challenge 100
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-muted-foreground">
                    {t('methodology.comparison.traditional', 'Traditional')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_KEYS.map(key => (
                  <tr key={key} className="border-b">
                    <td className="py-4 px-4 font-medium">
                      {t(`methodology.comparison.rows.${key}.label`)}
                    </td>
                    <td className="py-4 px-4 text-primary">
                      {t(`methodology.comparison.rows.${key}.challenge100`)}
                    </td>
                    <td className="py-4 px-4 text-muted-foreground">
                      {t(`methodology.comparison.rows.${key}.traditional`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Data Transparency */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {t('methodology.transparencyTitle', 'Transparency & Metrics')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {Object.entries(entityBlueprint.metrics).map(([key, metric]) => (
              <Card key={key} className="text-center">
                <CardContent className="pt-6">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                    {metric.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {lang === 'zh' ? metric.labelZh : metric.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center p-8 bg-muted/30 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">
            {t('methodology.ctaTitle', 'Ready to Start Your Challenge 100?')}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            {t('methodology.ctaDescription', 'Join the journey of building 100 products through systematic methodology.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" onClick={() => navigate({ to: getLocalizedPath('/challenge100') })}>
              <Target className="mr-2 h-5 w-5" />
              {t('methodology.ctaButton', 'Explore Challenge 100')}
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate({ to: getLocalizedPath('/#contact') })}>
              {t('methodology.ctaContact', 'Contact Me')}
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
